import {
  githubRepoRegexCheck,
  gitlabRepoRegexCheck,
  sanitizeRepo,
  replaceBlobWithRaw,
} from "components/utils";
import agent, {
  LaunchableFileRequest,
  LaunchableFromList,
  LaunchablePort,
  type LaunchableParameter,
  type NGCAPIKeySpec,
} from "server";
import { useState, useEffect, useCallback } from "react";
import { parsePortMappingEntry } from "components/Environment/utils";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import yaml from "js-yaml";

import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import { validateRemoteFile } from "server/devplane/remotefiles/api";

const COLAB_HOSTNAME = "colab.research.google.com";

export const isColabUrl = (fileName: string): boolean => {
  try {
    return new URL(fileName).hostname === COLAB_HOSTNAME;
  } catch {
    return false;
  }
};

export const formatShortFileName = (
  fileName: string,
  validatedColabFileName?: string,
): string => {
  const slashIndex = fileName.lastIndexOf("/");
  const dotPynbIndex = fileName.lastIndexOf(".ipynb");
  if (slashIndex === -1) return "";

  const shortFileName = fileName.slice(
    slashIndex + 1,
    dotPynbIndex !== -1 ? dotPynbIndex : undefined,
  );
  if (isColabUrl(fileName)) {
    return `Colab: ${validatedColabFileName || "Colab File"}`;
  }
  if (fileName.includes("ipynb")) return `Notebook: ${shortFileName}`;
  if (fileName.includes("github.com")) {
    return `GitHub Repo: ${sanitizeRepo(shortFileName)}`;
  }
  if (fileName.includes("gitlab.com")) {
    return `GitLab Repo: ${sanitizeRepo(shortFileName)}`;
  }
  return "";
};

export enum FileType {
  Notebook,
  Markdown,
  Colab,
  GithubRepo,
  GitlabRepo,
  Unknown,
}

export function getFileType(fileName: string | null | undefined): FileType {
  if (fileName?.endsWith(".ipynb")) {
    return FileType.Notebook;
  }
  if (fileName?.includes("colab")) {
    return FileType.Colab;
  }
  if (githubRepoRegexCheck(fileName || "")) {
    return FileType.GithubRepo;
  }
  if (gitlabRepoRegexCheck(fileName || "")) {
    return FileType.GitlabRepo;
  }
  if (fileName?.endsWith(".md")) {
    return FileType.Markdown;
  }

  return FileType.Unknown;
}

export const validateFileUrl = (value: string): string | null => {
  if (value.endsWith("/")) {
    return "Invalid URL: Please remove the trailing slash.";
  } else if (
    value.match(
      /^(https:\/\/github\.com\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/tree\/[A-Za-z0-9_.-]+(?:\/[\S]*)?$|https:\/\/gitlab\.com\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+\/-\/tree\/[A-Za-z0-9_.-]+(?:\/[\S]*)?$)/,
    )
  ) {
    return "Invalid URL: The provided link points to a directory, not a repository or file. Please provide a GitHub or GitLab repository or a file URL in raw or blob format";
  } else {
    return null;
  }
};

export const validatePublicRemoteFile = async (
  value: string,
): Promise<string | null> => {
  const localValidationError = validateFileUrl(value);
  if (localValidationError) return localValidationError;

  try {
    const result = await validateRemoteFile(replaceBlobWithRaw(value));
    if (!result.public) {
      return (
        result.errorMessage ||
        "The remote file is private, unsupported, or unavailable"
      );
    }
  } catch (error) {
    return error instanceof Error
      ? error.message
      : "Failed to validate the remote file";
  }

  return null;
};

export const getLaunchableUrl = (launchable: LaunchableFromList): string => {
  if (launchable.rawURL === "")
    return `${window.location.origin}/launchable/deploy?launchableID=${launchable.id}`;
  let newUrl = new URL(launchable.rawURL || "");
  newUrl.searchParams.append("launchableID", launchable.id);
  return newUrl.toString();
};

export interface ProcessedLaunchableData {
  instanceType: string | null;
  cloudCredId: string | null;
  location: string | null;
  subLocation: string | null;
  containerSelected: ContainerSelected;
  cuda: string | null;
  python: string | null;
  diskStorage: string | null;
  file: string | null;
  ports: Record<string, string> | null;
  firewallRules: CreateFirewallRule[] | null;
  userId: string | null;
  orgId: string | null;
  name: string | null;
  description: string | null;
  launchableId: string | null;
  url: string;
  couponCode: string | null;
  viewAccess: "public" | "organization" | "published" | null;
  imageId: string | null;
  performDefaultBuildForCustomImage: boolean;
  parameters: LaunchableParameter[];
  /** True when the launchable has at least one port with cta-enabled: "true". */
  hasCTAApp: boolean;
}

export const getLaunchableNgcApiKeySpec = (
  launchable?: LaunchableFromList | null,
): NGCAPIKeySpec | null =>
  launchable?.ngcApiKeySpec ??
  launchable?.createWorkspaceRequest?.ngcApiKeySpec ??
  null;

export const getLaunchableParameters = (
  launchable?: LaunchableFromList | null,
): LaunchableParameter[] => {
  if (!launchable) return [];
  const rawLaunchable = launchable as any;
  return (
    launchable.buildRequest?.parameters ??
    rawLaunchable.build?.parameters ??
    rawLaunchable.environment?.build?.parameters ??
    []
  );
};

export const transformPortListToMap = (
  ports: LaunchablePort[],
): Record<string, string> | null => {
  if (!ports || ports.length === 0) {
    return null;
  }
  const portMap: Record<string, string> = {};
  ports.forEach((port) => {
    portMap[port.name] = port.port;
  });
  return portMap;
};

export const transformPortMapToList = (
  portMap: Record<string, string> | null,
): LaunchablePort[] => {
  if (!portMap) {
    return [];
  }
  return Object.entries(portMap).map(([name, port]) => ({
    name,
    port,
  }));
};

export const processLaunchableData = (
  data: LaunchableFromList,
): ProcessedLaunchableData | null => {
  // New way doesn't parse rawURL anymore but still needs to be backwards compatible
  if (!data.rawURL) {
    // Plumb together the containerSelected object
    let pythonVersion = "3.10";
    let cudaVersion = "12.0.1";
    let containerSelected: ContainerSelected = {
      verbBuild: {
        containerUrl: "Default",
      },
    };

    if (data.buildRequest?.vmBuild) {
      containerSelected = {
        vmBuild: data.buildRequest.vmBuild,
      };
    } else if (
      data.buildRequest?.verbBuild &&
      data.buildRequest?.verbBuild.verbYaml != ""
    ) {
      const verbYaml = yaml.load(data.buildRequest?.verbBuild.verbYaml);
      if (verbYaml?.build?.base_image) {
        containerSelected = {
          verbBuild: {
            containerUrl: verbYaml.build.base_image,
          },
        };
      } else {
        containerSelected = {
          verbBuild: {
            containerUrl: "Default",
          },
        };
        pythonVersion = verbYaml?.build?.python_version || "3.10";
        cudaVersion = verbYaml?.build?.cuda || "12.0.1";
      }
    } else if (
      data.buildRequest?.containerBuild?.containerUrl &&
      data.buildRequest?.containerBuild?.containerUrl != ""
    ) {
      const containerUrl = data.buildRequest.containerBuild.containerUrl;
      containerSelected = {
        customContainer: {
          containerUrl: containerUrl,
          entryPoint: data.buildRequest.containerBuild.entryPoint || "",
        },
      };
    } else if (data.buildRequest?.dockerCompose) {
      containerSelected = {
        dockerCompose: {
          fileUrl: data.buildRequest.dockerCompose.fileUrl,
          jupyterInstall: data.buildRequest.dockerCompose.jupyterInstall,
          yamlString: data.buildRequest.dockerCompose.yamlString,
        },
      };
    }

    return {
      instanceType: data.createWorkspaceRequest?.instanceType || "",
      cloudCredId:
        data.createWorkspaceRequest?.cloudCredId ||
        data.createWorkspaceRequest?.cloud_cred_id ||
        null,
      location: data.createWorkspaceRequest?.location || null,
      subLocation: data.createWorkspaceRequest?.subLocation || null,
      diskStorage: data.createWorkspaceRequest?.storage || "",
      containerSelected: containerSelected,
      cuda: cudaVersion,
      python: pythonVersion,
      file: data.file?.url || "",
      ports: transformPortListToMap(data.buildRequest?.ports || []),
      firewallRules: data.createWorkspaceRequest?.firewallRules || null,
      userId: data.createdByUserId || null,
      orgId: data.createdByOrgId || null,
      name: data.name || null,
      launchableId: data.id || null,
      description: data.description || null,
      url: getLaunchableUrl(data),
      couponCode: data.couponCode || null,
      parameters: getLaunchableParameters(data),
      hasCTAApp: (data.buildRequest?.ports ?? []).some(
        (p) => p.labels?.["cta-enabled"] === "true",
      ),
      // Map old values for backward compatibility
      viewAccess: (() => {
        const access = (data as any).viewAccess;
        if (access === "link") return "public";
        if (access === "marketplace") return "published";
        return access || "public";
      })(),
      imageId: data.createWorkspaceRequest?.imageId || null,
      performDefaultBuildForCustomImage:
        data.createWorkspaceRequest?.performDefaultBuildForCustomImage ?? false,
    };
  } else {
    let searchParams = "";
    try {
      searchParams = new URL(data.rawURL || "").search;
    } catch (error) {
      console.error("Invalid URL", error);
      return null;
    }
    // If no version field, grab from query params
    const query = new URLSearchParams(searchParams);
    const ports = query.get("ports");
    const mappedPortsFromString = parsePortMappingEntry(ports || "");

    return {
      instanceType: query.get("instance"),
      cloudCredId: query.get("cloudCredId") || query.get("cloudCredID"),
      location: null,
      subLocation: null,
      containerSelected: {
        verbBuild: {
          containerUrl: query.get("baseImage") || "Default",
        },
      },
      cuda: query.get("cuda"),
      python: query.get("python"),
      diskStorage: query.get("diskStorage"),
      file: query.get("file"),
      ports: mappedPortsFromString,
      firewallRules: [],
      userId: query.get("userID"),
      orgId: query.get("orgID"),
      name: query.get("name"),
      launchableId: query.get("launchableID"),
      url: getLaunchableUrl(data),
      couponCode: query.get("couponCode"),
      imageId: null,
      performDefaultBuildForCustomImage: false,
      description: "link",
      parameters: [],
      hasCTAApp: false,
      viewAccess: (() => {
        const accessLevel = query.get("viewAccess");
        // Map old values for backward compatibility
        if (accessLevel === "link") return "public";
        if (accessLevel === "marketplace") return "published";
        if (accessLevel === "public") return "public";
        return (
          (accessLevel as "public" | "organization" | "published") || "public"
        );
      })(),
    };
  }
};

interface UseLaunchableResult {
  launchableData: ProcessedLaunchableData | null;
  rawLaunchableData: LaunchableFromList | null;
  error: string | null;
  loading: boolean;
  launchableNotFound: boolean;
  launchableAccessError: boolean;
  refetch: () => Promise<void>;
}

export const useLaunchable = (
  launchableId: string,
  isPublic: boolean,
): UseLaunchableResult => {
  const [launchableData, setLaunchableData] =
    useState<ProcessedLaunchableData | null>(null);
  const [rawLaunchableData, setRawLaunchableData] =
    useState<LaunchableFromList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [launchableNotFound, setLaunchableNotFound] = useState<boolean>(false);
  const [launchableAccessError, setLaunchableAccessError] =
    useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let response;
      if (isPublic) {
        response = await agent.Launchables.getPublicLaunchable(launchableId);
      } else {
        response = await agent.Launchables.getLaunchableWithAuth(launchableId);
      }

      if (response && response.success && response.data) {
        setRawLaunchableData(response.data);
        const processedResult = processLaunchableData(response.data);
        if (!processedResult) {
          setError("Error processing launchable data");
          setLoading(false);
          return;
        }

        setLaunchableData(processedResult);
        setLoading(false);
      } else {
        setError(response.message || "Error fetching launchable data");
        if (response.message?.includes("environment not found")) {
          setLaunchableNotFound(true);
        }
        if (response.message?.includes("Access Denied:")) {
          setLaunchableAccessError(true);
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [launchableId, isPublic, refetchTrigger]);

  const refetch = useCallback(async () => {
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  return {
    launchableData,
    rawLaunchableData,
    error,
    loading,
    launchableNotFound,
    launchableAccessError,
    refetch,
  };
};

export const createFileRequest = (
  file: string,
  containerSelected?: ContainerSelected,
): LaunchableFileRequest[] | null => {
  if (file === "") {
    return null;
  }

  const path = !!containerSelected?.verbBuild?.containerUrl
    ? "./verb-workspace"
    : "./";

  return [{ url: replaceBlobWithRaw(file), path }];
};
